/**
 * The "show us" half of the Report an Issue widget (ISS-485).
 *
 * Every context option is independent and they COMBINE: the automatic
 * capture of this screen (a toggle, on by default), any number (up to the cap)
 * of the user's own images, and "point to it". Every image thumbnail is a
 * button that opens the annotator; the edited image replaces the original.
 */
import type { ChangeEvent } from 'react';
import { useRef } from 'react';
import { ImagePlus, Loader2, Target, X, Pencil, Camera } from 'lucide-react';
import type { SelectedElement } from '../../types/issue-triage';
import type { CaptureStatus } from './useBackgroundScreenCapture';

export interface ReportAttachment {
  id: string;
  dataUrl: string;
}

interface ReportContextTrayProps {
  /** Why the capture starts off here (a sensitive page), shown under the toggle. */
  captureOffReason?: string;
  captureEnabled: boolean;
  onToggleCapture: (enabled: boolean) => void;
  capture: string | null;
  captureStatus: CaptureStatus;
  attachments: ReportAttachment[];
  canAddMore: boolean;
  isAttaching: boolean;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  /** 'capture' or an attachment id. */
  onEdit: (target: string) => void;
  selectedElement: SelectedElement | null;
  onPointToIt: () => void;
  onClearElement: () => void;
}

const THUMB = 'relative h-20 w-28 shrink-0 overflow-hidden rounded-xl ring-1 ring-slate-200 bg-slate-50';
const EDIT_BADGE =
  'pointer-events-none absolute bottom-1 right-1 rounded-full bg-slate-900/70 p-1 text-white';

export function ReportContextTray(props: ReportContextTrayProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) props.onAddFiles(files);
    // Allow re-selecting the same file after a rejection or removal.
    e.target.value = '';
  };

  const showCaptureThumb = props.captureEnabled && props.capture;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={props.captureEnabled}
            onChange={(e) => props.onToggleCapture(e.target.checked)}
          />
          <Camera className="h-4 w-4 text-[#007ACC]" aria-hidden="true" />
          Include a capture of this screen
        </label>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!props.canAddMore || props.isAttaching}
          className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {props.isAttaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 text-emerald-600" />}
          Add your own photos
        </button>
        <button
          type="button"
          onClick={props.onPointToIt}
          className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
        >
          <Target className="h-4 w-4 text-purple-600" />
          Point to it
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          aria-label="Add photos"
          onChange={handleFileInput}
        />
      </div>

      {props.captureOffReason && <p className="text-xs text-amber-800">{props.captureOffReason}</p>}

      {(showCaptureThumb || props.captureEnabled || props.attachments.length > 0) && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {props.captureEnabled && !props.capture && props.captureStatus === 'capturing' && (
            <div className={`${THUMB} flex items-center justify-center`} aria-live="polite">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              <span className="sr-only">Capturing this screen…</span>
            </div>
          )}
          {props.captureEnabled && !props.capture && props.captureStatus === 'failed' && (
            <div className={`${THUMB} flex items-center justify-center px-2 text-center text-xs text-slate-500`}>
              Could not capture this screen
            </div>
          )}
          {showCaptureThumb && (
            <button type="button" className={THUMB} onClick={() => props.onEdit('capture')} aria-label="Edit screen capture">
              <img src={props.capture ?? ''} alt="Screen capture" className="h-full w-full object-cover" />
              <span className={EDIT_BADGE}><Pencil className="h-3 w-3" /></span>
            </button>
          )}
          {props.attachments.map((a, i) => (
            <div key={a.id} className="relative shrink-0">
              <button
                type="button"
                className={THUMB}
                onClick={() => props.onEdit(a.id)}
                aria-label={`Edit attached image ${i + 1}`}
              >
                <img src={a.dataUrl} alt={`Attached image ${i + 1}`} className="h-full w-full object-cover" />
                <span className={EDIT_BADGE}><Pencil className="h-3 w-3" /></span>
              </button>
              <button
                type="button"
                onClick={() => props.onRemoveAttachment(a.id)}
                aria-label={`Remove attached image ${i + 1}`}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900/80 p-0.5 text-white hover:bg-slate-900"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {props.selectedElement && (
        <div className="flex items-start gap-2 rounded-xl bg-purple-50 p-3 ring-1 ring-purple-200">
          <Target className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
          <div className="min-w-0 flex-1">
            <code className="break-all text-xs text-purple-800">{props.selectedElement.selector}</code>
            {props.selectedElement.textContent && (
              <p className="mt-1 truncate text-xs text-purple-600">"{props.selectedElement.textContent}"</p>
            )}
          </div>
          <button
            type="button"
            onClick={props.onClearElement}
            className="rounded-full p-1 text-purple-400 hover:bg-purple-100 hover:text-purple-600"
            aria-label="Remove selected element"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Tip: click any image to draw on it or add text. You can also drag &amp; drop or paste images here.
      </p>
    </div>
  );
}
